import type { ChatAttachment, ChatMessage } from '@/api/types/MessagingApi'
import { PhotoViewer } from '@/components/SupportPage/PhotoViewer'
import { colors } from '@/constants/colors'
import { Pressable } from '@/ui/core/pressable'
import { Text } from '@/ui/core/typography'
import { cn } from '@/ui/utils'
import { Ionicons } from '@expo/vector-icons'
import { differenceInMinutes, format, isThisWeek, isToday, isYesterday } from 'date-fns'
import { Image } from 'expo-image'
import * as WebBrowser from 'expo-web-browser'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { ScrollView, View } from 'react-native'

/** A new run of bubbles starts when the side changes or this much time has passed. */
const GROUP_GAP_MINUTES = 5
/** A time label appears above a message this far from the one before it. */
const STAMP_GAP_MINUTES = 60

/** "Today 12:39 PM", "Yesterday 9:04 AM", "Tuesday 3:15 PM", "Sep 3, 12:39 PM". */
const stamp = (iso: string) => {
	const time = format(iso, 'h:mm a')
	if (isToday(iso)) return `Today ${time}`
	if (isYesterday(iso)) return `Yesterday ${time}`
	if (isThisWeek(iso)) return `${format(iso, 'EEEE')} ${time}`
	return `${format(iso, 'MMM d')}, ${time}`
}

const isImage = (attachment: ChatAttachment) =>
	!!attachment.url && !!attachment.contentType?.startsWith('image/')

/**
 * A photo inline, a file as a named line. A tap opens a photo in the viewer and a file in the
 * system's own in-app browser, which shows PDFs and text without leaving Wally. Nothing opens
 * until the file has a link, so a photo still on its way stays put.
 */
const Attachment = ({
	attachment,
	mine,
	onView,
}: {
	attachment: ChatAttachment
	mine: boolean
	onView: (uri: string, label: string) => void
}) => {
	// A photo can be looked at from its local copy; a file needs its link from the server.
	const url = attachment.url ?? null
	const remote = !!url && !url.startsWith('file:')
	if (isImage(attachment)) {
		return (
			<Pressable
				onPress={() => url && onView(url, attachment.filename)}
				disabled={!url}
				accessibilityRole="imagebutton"
				accessibilityLabel={attachment.filename}
				haptic={false}
			>
				<Image
					source={{ uri: attachment.url ?? undefined }}
					contentFit="cover"
					style={{ width: 220, height: 160, borderRadius: 16 }}
				/>
			</Pressable>
		)
	}
	return (
		<Pressable
			onPress={() => url && WebBrowser.openBrowserAsync(url)}
			disabled={!remote}
			accessibilityRole="button"
			accessibilityLabel={`Open ${attachment.filename}`}
			haptic={false}
			className="flex-row items-center gap-1.5 px-1.5 py-1"
		>
			<Ionicons
				name="document-outline"
				size={14}
				color={mine ? colors.white : colors.ink}
			/>
			<Text size="body2" tone={mine ? 'white' : 'ink'} numberOfLines={1} underlined>
				{attachment.filename}
			</Text>
		</Pressable>
	)
}

/**
 * One message. The side says who: Wally in white, the patient in ink. A floor on the width keeps
 * a one-word reply from turning into a circle. A send in flight sits at 60%.
 */
const Bubble = ({
	message,
	onView,
	onRetry,
}: {
	message: ChatMessage
	onView: (uri: string, label: string) => void
	onRetry: (message: ChatMessage) => void
}) => {
	const mine = message.direction === 'INCOMING'
	const withFiles = !!message.attachments?.length
	const body = (
		<View
			className={cn(
				'min-w-[54px] max-w-[80%] rounded-note',
				// Words alone centre in the floor with 16 at the sides. With a photo the bubble
				// holds the photo at an even 10 all round, and the words indent to their usual 16.
				withFiles ? 'items-start p-2.5' : 'items-center px-4 py-2.5',
				mine ? 'self-end bg-ink' : 'self-start bg-white',
				message.pending && 'opacity-60'
			)}
			// A failed bubble is one target that retries, so a tap on its photo cannot open it.
			pointerEvents={message.failed ? 'none' : 'auto'}
			accessible={!message.failed}
			accessibilityLabel={`${mine ? 'You' : 'Wally'}: ${message.body ?? ''}${
				withFiles
					? `, ${message.attachments?.length} attachment${
							withFiles && message.attachments?.length === 1 ? '' : 's'
					  }`
					: ''
			}`}
		>
			{!!message.body && (
				<Text
					size="body0"
					tone={mine ? 'white' : 'ink'}
					className={cn(withFiles && 'px-1.5 pt-1 pb-3')}
				>
					{message.body}
				</Text>
			)}
			{(message.attachments ?? []).map((attachment) => (
				<Attachment
					key={attachment.assetId ?? attachment.url ?? attachment.filename}
					attachment={attachment}
					mine={mine}
					onView={onView}
				/>
			))}
		</View>
	)
	if (!message.failed) return body
	return (
		<Pressable
			onPress={() => onRetry(message)}
			accessibilityRole="button"
			accessibilityLabel="Retry sending"
			haptic={false}
			className={cn('max-w-full', mine ? 'items-end' : 'items-start')}
		>
			{body}
		</Pressable>
	)
}

/** A run of one side's messages, then a status line when the last is in flight or failed. */
const Group = ({
	messages,
	onRetry,
	onView,
}: {
	messages: ChatMessage[]
	onRetry: (message: ChatMessage) => void
	onView: (uri: string, label: string) => void
}) => {
	const last = messages[messages.length - 1]
	const mine = last?.direction === 'INCOMING'
	return (
		<View className={cn('w-full', mine ? 'items-end' : 'items-start')} style={{ gap: 6 }}>
			{messages.map((message) => (
				<Bubble key={message.id} message={message} onView={onView} onRetry={onRetry} />
			))}
			{last?.pending && (
				<Text size="body2" tone="soft" className="mt-0.5 pr-1">
					Sending…
				</Text>
			)}
			{last?.failed && (
				<Pressable
					onPress={() => onRetry(last)}
					accessibilityRole="button"
					accessibilityLabel="Retry sending"
					haptic={false}
					hitSlop={10}
					className="mt-0.5 pr-1"
				>
					<Text size="body2" weight="bold" style={{ color: colors.accent }}>
						Didn't send · Tap to retry
					</Text>
				</Pressable>
			)}
		</View>
	)
}

/**
 * A time label between two hairlines in its own colour, the whole rule kept quiet. The first one
 * in the thread stands alone: there is nothing above it for a rule to separate.
 */
const Stamp = ({ iso, first }: { iso: string; first: boolean }) => (
	<View className="w-full flex-row items-center px-3" style={{ gap: 10 }}>
		{!first && <View className="h-px flex-1 bg-edge opacity-70" />}
		<Text
			size="body3"
			tone="soft"
			className={cn('tracking-[0.08em]', first && 'flex-1 text-center')}
		>
			{stamp(iso)}
		</Text>
		{!first && <View className="h-px flex-1 bg-edge opacity-70" />}
	</View>
)

/** Messages in order become runs by side, with a time label wherever an hour or more has passed. */
const layOut = (
	messages: ChatMessage[],
	onRetry: (m: ChatMessage) => void,
	onView: (uri: string, label: string) => void
) => {
	const out: ReactNode[] = []
	let run: ChatMessage[] = []
	const flush = () => {
		if (run.length) {
			out.push(
				<Group key={`g${run[0]?.id}`} messages={run} onRetry={onRetry} onView={onView} />
			)
			run = []
		}
	}
	messages.forEach((message, index) => {
		const previous = messages[index - 1]
		const minutes = previous
			? differenceInMinutes(message.createdAt, previous.createdAt)
			: Number.POSITIVE_INFINITY
		if (minutes >= STAMP_GAP_MINUTES) {
			flush()
			out.push(
				<Stamp key={`s${message.id}`} iso={message.createdAt} first={index === 0} />
			)
		} else if (
			previous &&
			(previous.direction !== message.direction ||
				minutes >= GROUP_GAP_MINUTES ||
				previous.failed ||
				previous.pending)
		) {
			flush()
		}
		run.push(message)
	})
	flush()
	return out
}

/** The thread, oldest at the top, kept scrolled to the newest message as it grows. */
export const ChatThread = ({
	messages,
	onRetry,
	header,
	bottomInset = 0,
}: {
	messages: ChatMessage[]
	onRetry: (message: ChatMessage) => void
	/** What sits at the top of the thread: the opener, when there is nothing else to show. */
	header?: ReactNode
	/** Room at the foot for whatever floats over the thread's end: the composer's height. */
	bottomInset?: number
}) => {
	const scrollRef = useRef<ScrollView>(null)
	const [viewing, setViewing] = useState<{ uri: string; label: string } | null>(null)

	// biome-ignore lint/correctness/useExhaustiveDependencies: scroll on every change to the list
	useEffect(() => {
		scrollRef.current?.scrollToEnd({ animated: true })
	}, [messages.length])
	// The composer's height arrives after the first paint; keep the end in view once it does.
	// biome-ignore lint/correctness/useExhaustiveDependencies: re-scroll when the inset changes
	useEffect(() => {
		scrollRef.current?.scrollToEnd({ animated: false })
	}, [bottomInset])

	return (
		<View className="flex-1">
			<ScrollView
				ref={scrollRef}
				className="flex-1"
				contentContainerStyle={{
					paddingHorizontal: 16,
					paddingTop: 20,
					paddingBottom: bottomInset + 4,
					gap: 14,
				}}
				keyboardShouldPersistTaps="handled"
				// The indicator lives inside the card's corner radius at the top and above the composer.
				scrollIndicatorInsets={{ top: 24, bottom: bottomInset, right: 2 }}
				onContentSizeChange={() => scrollRef.current?.scrollToEnd({ animated: false })}
			>
				{header}
				{layOut(messages, onRetry, (uri, label) => setViewing({ uri, label }))}
			</ScrollView>
			<PhotoViewer
				uri={viewing?.uri ?? null}
				label={viewing?.label}
				onClose={() => setViewing(null)}
			/>
		</View>
	)
}
